export interface SummaryTokenUsage {
  billedTokens: number;
  actualTokens: number;
  requestCount: number;
}

export interface ToolQuotaUsage {
  limit: number;
  used: number;
  remaining: number;
  period: "daily" | "weekly" | string;
  periodKey: string;
  resetAt?: string;
}

export interface ModelUsageItem {
  modelName: string;
  requestCount: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  totalCost: number;
}

export interface ProjectUsageOverview {
  projectId: number;
  period: { start: string; end: string };
  summary: SummaryTokenUsage;
  toolPlan: string;
  toolFrozen: boolean;
  toolQuotas: Record<string, ToolQuotaUsage>;
  modelTotals: {
    requestCount: number;
    totalTokens: number;
    totalCost: number;
  };
  models: ModelUsageItem[];
}

export interface ToolQuotaCard extends ToolQuotaUsage {
  scenario: string;
  label: string;
  scope: "account";
}

export interface UsageFailure {
  kind: "error" | "forbidden" | "plan-limit";
  message: string;
  traceId?: string;
}

interface UsageErrorBody {
  msg?: string;
  message?: string;
  traceId?: string;
  data?: {
    code?: string;
    messageKey?: string;
    traceId?: string;
  };
}

const TOOL_QUOTA_LABELS: Record<string, string> = {
  agent_run_daily: "Agent 每日运行",
  workflow_run_daily: "工作流每日运行",
  mcp_tool_weekly: "MCP 工具每周调用",
};

const TOOL_QUOTA_ORDER = ["agent_run_daily", "workflow_run_daily", "mcp_tool_weekly"];

export const buildToolQuotaCards = (
  quotas: Record<string, ToolQuotaUsage> = {}
): ToolQuotaCard[] =>
  TOOL_QUOTA_ORDER.flatMap((scenario) => {
    const quota = quotas[scenario];
    return quota
      ? [{ ...quota, scenario, label: TOOL_QUOTA_LABELS[scenario], scope: "account" as const }]
      : [];
  });

export const quotaPercent = (quota: ToolQuotaUsage): number => {
  if (quota.limit <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((quota.used / quota.limit) * 100)));
};

export const quotaDisplay = (quota: ToolQuotaUsage): string =>
  quota.limit <= 0
    ? `${quota.used.toLocaleString("zh-CN")} / 不限`
    : `${quota.used.toLocaleString("zh-CN")} / ${quota.limit.toLocaleString("zh-CN")}`;

export const classifyUsageFailure = (
  status?: number,
  body: UsageErrorBody = {},
  headerTraceId?: string
): UsageFailure => {
  const detail = body.data || {};
  const traceId = headerTraceId || detail.traceId || body.traceId;
  const message = body.msg || body.message || "获取用量失败";
  if (detail.code === "PLAN_LIMIT_REACHED" || detail.messageKey?.startsWith("entitlement.")) {
    return { kind: "plan-limit", message, traceId };
  }
  if (status === 403) return { kind: "forbidden", message, traceId };
  return { kind: "error", message, traceId };
};
